import './GiftCardInventory.css';

import DataTable from 'react-data-table-component';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Form, Spinner, Table } from 'react-bootstrap';
import {
    GET_SKUS_FOR_PROPERTY,
    GET_SKU_BY_ID,
    GET_GC_INVENTORIES_FOR_PROPERTY,
    GET_GC_INVENTORIES_BY_SKUS_FOR_PROPERTY,
    GET_GC_INVENTORY_BY_ID,
    GET_GC_INVENTORY_EMAIL_LIST_FOR_PROPERTY,
    CREATE_GC_INVENTORY,
    UPDATE_GC_INVENTORY,
    DELETE_GC_INVENTORY,
    APPROVE_GC_INVENTORY,
    GET_AUDIT_COUNT_FOR_OPERATION_GROUPED_BY_OBJECT,
    GET_AUDITS_FOR_OBJECT_FILTERED_BY_OPERATION,
    useQuery,
    useLazyQuery,
    useMutation,
} from 'utils/graphql';
import { formatUTCDateTime } from 'utils/date.util';
import User from 'utils/user.util';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import InfoModal from 'components/Modals/InfoModal/InfoModal';
import useToken from 'components/useToken';

import { ReactComponent as CheckIcon } from 'assets/icons/check_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';

const ModalModes = {
    Create: 1,
    Edit: 2,
    ReadOnly: 3,
};

const GiftCardInventory = ({ property }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const csvLinkRef = useRef(null);

    const [skus, setSkus] = useState([]);
    const [gcInventories, setGcInventories] = useState([]);
    const [cachedGroupedGcInventories, setCachedGroupedGcInventories] = useState({});
    const [totalGcInventoryCount, setTotalGcInventoryCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState(ModalModes.Create);
    const [gcInventoryToEdit, setGcInventoryToEdit] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formErrors, setFormErrors] = useState({
        activations: '',
        destroyed: '',
        lost: '',
        transferred: '',
        received: '',
        onHand: '',
        reason: '',
        gcInventory: '',
    });
    const [validated, setValidated] = useState(false);
    const [skuIndex, setSkuIndex] = useState(0);
    const [activations, setActivations] = useState(0);
    const [destroyed, setDestroyed] = useState(0);
    const [lost, setLost] = useState(0);
    const [transferred, setTransferred] = useState(0);
    const [received, setReceived] = useState(0);
    const [comment, setComment] = useState('');
    const [onHand, setOnHand] = useState(0);
    const [onHandCalculated, setOnHandCalculated] = useState(0);
    const [discrepancy, setDiscrepancy] = useState(0);
    const [reason, setReason] = useState('');
    const [lastEntryForSku, setLastEntryForSku] = useState(null);
    const [existingEntryForSku, setExistingEntryForSku] = useState(null);

    const [isLoadingGcInventories, setIsLoadingGcInventories] = useState(true);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
    const [infoModalText, setInfoModalText] = useState('');
    const [infoModalOptions, setInfoModalOptions] = useState({});
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);
    const [gcInventoryToDelete, setGcInventoryToDelete] = useState(null);
    const [isLoadingFormModal, setIsLoadingFormModal] = useState(false);

    const [gcInventoryInAuditModal, setGcInventoryInAuditModal] = useState(null);
    const [auditModalTitle, setAuditModalTitle] = useState('');
    const [showAuditErrorMsg, setShowAuditErrorMsg] = useState(false);
    const [audits, setAudits] = useState([]);

    const [csvFileName, setCsvFileName] = useState('');
    const [csvData, setCsvData] = useState([]);
    const [isLoadingCsv, setIsLoadingCsv] = useState(false);
    const csvHeaders = [
        { label: 'Id', key: 'id' },
        { label: t('date'), key: 'date' },
        { label: t('sku'), key: 'sku' },
        { label: t('property'), key: 'property__c_id' },
        { label: t('comment'), key: 'comment' },
        { label: t('on_hand_short'), key: 'on_hand' },
        { label: t('activations'), key: 'activated' },
        { label: t('destroyed'), key: 'destroyed' },
        { label: t('lost'), key: 'lost' },
        { label: t('transferred'), key: 'transferred' },
        { label: t('received'), key: 'received' },
        { label: t('reason'), key: 'reason' },
        { label: t('counted_by'), key: 'counted_by' },
        { label: t('approved_by'), key: 'approved_by' },
    ];

    const { loading: isLoadingSkus } = useQuery(GET_SKUS_FOR_PROPERTY, {
        variables: {
            property_id: property.id,
        },
        onCompleted: (data) => {
            setSkus(data.table.records.map((record) => record.sku));
        },
    });

    const [getSkuById] = useLazyQuery(GET_SKU_BY_ID, {
        fetchPolicy: 'cache-and-network',
    });

    const [getGcInventoriesForProperty] = useLazyQuery(GET_GC_INVENTORIES_FOR_PROPERTY, {
        variables: {
            property_id: property.id,
        },
        fetchPolicy: 'cache-and-network',
    });

    const [getGcInventoryById] = useLazyQuery(GET_GC_INVENTORY_BY_ID, {
        fetchPolicy: 'cache-and-network',
    });

    const [getGcInventoryEmailListByProperty] = useLazyQuery(
        GET_GC_INVENTORY_EMAIL_LIST_FOR_PROPERTY,
        {
            fetchPolicy: 'cache-and-network',
        }
    );

    const [getAuditsForObject, { loading: isLoadingAudits }] = useLazyQuery(
        GET_AUDITS_FOR_OBJECT_FILTERED_BY_OPERATION,
        {
            variables: {
                operation: 'UPDATE',
            },
            fetchPolicy: 'cache-and-network',
        }
    );

    const [getGcInventoriesBySkusForProperty] = useLazyQuery(
        GET_GC_INVENTORIES_BY_SKUS_FOR_PROPERTY,
        {
            variables: {
                property_id: property.id,
            },
            fetchPolicy: 'cache-and-network',
        }
    );

    const [getAuditCounts] = useLazyQuery(GET_AUDIT_COUNT_FOR_OPERATION_GROUPED_BY_OBJECT, {
        variables: {
            operation: 'UPDATE',
        },
        fetchPolicy: 'cache-and-network',
    });

    const [createGcInventoryMutation] = useMutation(CREATE_GC_INVENTORY, {
        variables: {
            counted_by: User.getUser()?.id,
            property__c_id: property.id,
        },
    });

    const [updateGcInventoryMutation] = useMutation(UPDATE_GC_INVENTORY, {
        variables: {
            counted_by: User.getUser()?.id,
        },
    });

    const [deleteGcInventoryMutation] = useMutation(DELETE_GC_INVENTORY);

    const [approveGcInventoryMutation] = useMutation(APPROVE_GC_INVENTORY, {
        variables: {
            approved_by: User.getUser()?.id,
        },
    });

    const fetchSkuById = async (id) => {
        const { data } = await getSkuById({ variables: { id } });
        return data?.table?.records?.[0]?.sku || null;
    };

    const setGcInventorySku = async (gcInventory, shortForm = false) => {
        const _gcInventory = { ...gcInventory };
        let sku = skus.find((sku) => sku.id === gcInventory.sku);

        // In some cases, the SKU might not be in the list of SKUs for the property, so we fetch it individually
        if (!sku) {
            sku = await fetchSkuById(gcInventory.sku);
        }

        _gcInventory.sku = shortForm ? sku.description : sku;
        return _gcInventory;
    };

    const parseGcInventoriesResponse = async (data, isForCsv = false) => {
        return Promise.all(
            data.table.records.map(async (record) => {
                const gcInventory = record.gc_inventory;
                let _gcInventory = {
                    ...gcInventory,
                    on_hand: parseInt(gcInventory.on_hand),
                    activated: gcInventory.activated ? parseInt(gcInventory.activated) : '',
                    destroyed: gcInventory.destroyed ? parseInt(gcInventory.destroyed) : '',
                    lost: gcInventory.lost ? parseInt(gcInventory.lost) : '',
                    transferred: gcInventory.transferred ? parseInt(gcInventory.transferred) : '',
                    received: gcInventory.received ? parseInt(gcInventory.received) : '',
                };

                if (isForCsv) {
                    _gcInventory.property__c_id = property.name;
                } else {
                    _gcInventory.isApproving = false;
                    _gcInventory.isDeleting = false;
                    _gcInventory.revisions = null;
                    _gcInventory.onHandCalculated = null;
                }

                if (
                    _gcInventory.counted_by_propertyuser__c_list &&
                    _gcInventory.counted_by_propertyuser__c_list.length > 0
                ) {
                    _gcInventory.counted_by =
                        _gcInventory.counted_by_propertyuser__c_list[0].propertyuser__c.name;
                } else {
                    _gcInventory.counted_by = t('deleted_user');
                }
                delete _gcInventory.counted_by_propertyuser__c_list;

                if (
                    _gcInventory.approved_by_propertyuser__c_list &&
                    _gcInventory.approved_by_propertyuser__c_list.length > 0
                ) {
                    _gcInventory.approved_by =
                        _gcInventory.approved_by_propertyuser__c_list[0].propertyuser__c.name;
                } else if (_gcInventory.approved_by) {
                    _gcInventory.approved_by = t('deleted_user');
                }
                delete _gcInventory.approved_by_propertyuser__c_list;

                return setGcInventorySku(_gcInventory, isForCsv);
            })
        );
    };

    const groupGcInventoriesBySkuOrderByDate = (gcInventories) => {
        const gcInventoryGroups = {};
        for (const gcInventory of gcInventories) {
            if (gcInventoryGroups[gcInventory.sku.id]) {
                gcInventoryGroups[gcInventory.sku.id].push(gcInventory);
            } else {
                gcInventoryGroups[gcInventory.sku.id] = [gcInventory];
            }
        }
        for (const skuId in gcInventoryGroups) {
            gcInventoryGroups[skuId].sort(
                (a, b) => new Date(`${b.date}Z`) - new Date(`${a.date}Z`)
            );
        }
        return gcInventoryGroups;
    };

    const loadGcInventoryOnHandCalculated = async (gcInventories) => {
        return getGcInventoriesBySkusForProperty({
            variables: {
                sku_ids: gcInventories.map((gcInventory) => gcInventory.sku.id),
            },
        }).then(async (response) => {
            if (response.error) return gcInventories;
            const groupedGcInventories = groupGcInventoriesBySkuOrderByDate(
                await Promise.all(
                    response.data.table.records.map((r) => setGcInventorySku(r.gc_inventory))
                )
            );
            const updatedGcInventories = [];
            const _cachedGroupedGcInventories = { ...cachedGroupedGcInventories };
            for (const gcInventory of gcInventories) {
                const gcInventoriesWithSku = groupedGcInventories[gcInventory.sku.id];
                // caching the grouped gc Inventories
                _cachedGroupedGcInventories[gcInventory.sku.id] = gcInventoriesWithSku;
                let onHandCalculated = 'N/A';
                if (gcInventoriesWithSku.length > 1) {
                    const lastEntry = findLastEntryForSku(gcInventoriesWithSku, gcInventory);
                    if (lastEntry) {
                        onHandCalculated =
                            parseInt(lastEntry.on_hand) -
                            parseInt(gcInventory.activated) -
                            parseInt(gcInventory.destroyed) -
                            parseInt(gcInventory.lost) -
                            parseInt(gcInventory.transferred) +
                            parseInt(gcInventory.received);
                    }
                }
                updatedGcInventories.push({ ...gcInventory, onHandCalculated });
            }
            setCachedGroupedGcInventories(_cachedGroupedGcInventories);
            return updatedGcInventories;
        });
    };

    const loadGcInventoryRevisionCounts = (gcInventories) => {
        getAuditCounts({
            variables: {
                objectIds: gcInventories.map((gcInventory) => gcInventory.id),
            },
        }).then((response) => {
            const updatedGcInventories = [];
            if (!response.errors) {
                const revisionCounts = response.data.auditCountForOperationGroupedByObject;
                for (const gcInventory of gcInventories) {
                    const revisionCount = revisionCounts.find((r) => r.id === gcInventory.id);
                    let count = 0;
                    if (revisionCount) {
                        count = revisionCount.count;
                        if (gcInventory.approved_by) {
                            count--;
                        }
                    }
                    updatedGcInventories.push({
                        ...gcInventory,
                        revisions: count,
                    });
                }
            } else {
                for (const gcInventory of gcInventories) {
                    updatedGcInventories.push({
                        ...gcInventory,
                        revisions: 'N/A',
                    });
                }
            }
            setGcInventories(updatedGcInventories);
        });
    };

    const columns = [
        {
            name: t('date'),
            sortable: false,
            maxWidth: '120px',
            selector: (row) => formatUTCDateTime(new Date(`${row.date}Z`), false),
        },
        {
            name: t('sku'),
            sortable: false,
            minWidth: '200px',
            selector: (row) => {
                return row.sku.description;
            },
        },
        {
            name: t('on_hand_short'),
            sortable: false,
            maxWidth: '100px',
            selector: (row) => parseInt(row.on_hand),
        },
        {
            name: t('on_hand_calculated'),
            sortable: false,
            selector: (row) => {
                if (!row.sku.detailed_form) {
                    return 'N/A';
                }
                if (!row.onHandCalculated) {
                    return <Spinner></Spinner>;
                } else {
                    return row.onHandCalculated;
                }
            },
        },
        {
            name: t('counted_by'),
            sortable: false,
            maxWidth: '200px',
            selector: (row) => row.counted_by,
        },
        {
            name: t('approved_by'),
            sortable: false,
            maxWidth: '200px',
            selector: (row) => (row.approved_by ? row.approved_by : t('pending')),
        },
        {
            name: t('revisions'),
            sortable: false,
            maxWidth: '90px',
            selector: (row) => {
                if (row.revisions == null) {
                    return <Spinner></Spinner>;
                } else if (row.revisions === 0) {
                    return row.revisions;
                } else {
                    return (
                        <a
                            href=""
                            onClick={(e) => {
                                e.preventDefault();
                                showAuditsModal(row);
                            }}
                        >
                            {row.revisions}
                        </a>
                    );
                }
            },
        },
        {
            name: t('manage'),
            sortable: false,
            ignoreRowClick: true,
            minWidth: '255px',
            selector: (row) => {
                if (row.approved_by) return;
                return (
                    <div className="d-flex align-items-center">
                        <LoadingButton
                            className="me-1"
                            isLoading={row.isApproving}
                            isDisabled={isDatatableDisabled()}
                            variant="success"
                            onClick={() => approveGcInventory(row)}
                        >
                            {t('approve')}
                        </LoadingButton>
                        <LoadingButton
                            isLoading={row.isDeleting}
                            isDisabled={isDatatableDisabled()}
                            variant="danger"
                            onClick={() => {
                                setGcInventoryToDelete(row);
                                setIsDeleteConfirmationModalOpen(true);
                            }}
                        >
                            {t('delete')}
                        </LoadingButton>
                    </div>
                );
            },
        },
    ];

    const showAuditsModal = (gcInventory) => {
        setShowAuditErrorMsg(false);
        setGcInventoryInAuditModal(gcInventory);

        setAuditModalTitle(
            t('gift_card_revisions_title', {
                skuDescription: gcInventory.sku.description,
                date: getGcinventoryMonthAndYear(gcInventory),
            })
        );
        getAuditsForObject({ variables: { objectId: gcInventory.id } })
            .then((response) => {
                if (!response.errors) {
                    let _audits = response.data.allAudits.edges.map((edge) => edge.node);
                    _audits.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    const audits = [];
                    for (const audit of _audits) {
                        const newValue = JSON.parse(audit.new.replace(/'/g, '"'));
                        const oldValue = JSON.parse(audit.old.replace(/'/g, '"'));
                        if (!(newValue.approved_by && !oldValue.approved_by)) {
                            audits.push({
                                ...oldValue,
                                date: formatUTCDateTime(new Date(`${oldValue.date}Z`), false),
                            });
                        }
                    }
                    setAudits(audits);
                } else {
                    setShowAuditErrorMsg(true);
                }
            })
            .catch((error) => {
                console.log({ error });
                setShowAuditErrorMsg(true);
            });
    };

    const getGcinventoryMonthAndYear = (gcInventory = null) => {
        const months = [
            'january',
            'february',
            'march',
            'april',
            'may',
            'june',
            'july',
            'august',
            'september',
            'october',
            'november',
            'december',
        ];
        const date = gcInventory ? new Date(`${gcInventory.date}Z`) : new Date();
        return `${t(months[date.getMonth()])} ${date.getFullYear()}`;
    };

    const assignModalTitle = (modalMode, gcInventoryToEdit = null) => {
        let titleKey;
        if (modalMode === ModalModes.Create) {
            titleKey = 'create_giftcard_inventory_for';
        } else if (modalMode === ModalModes.Edit) {
            titleKey = 'edit_giftcard_inventory';
        } else {
            titleKey = 'view_giftcard_inventory';
        }
        setModalTitle(`${t(titleKey)} ${getGcinventoryMonthAndYear(gcInventoryToEdit)}`);
    };

    const findLastEntryForSku = (relatedGcInventories, gcInventory = null) => {
        if (!relatedGcInventories) return null;

        const now = gcInventory ? new Date(`${gcInventory.date}Z`) : new Date();
        const yesterday = new Date(
            `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${(
                now.getDate() - 1
            )
                .toString()
                .padStart(2, '0')}T23:59:59.999Z`
        );
        const _lastEntryForSku = relatedGcInventories.find(
            (e) =>
                new Date(`${e.date}Z`).getTime() <= yesterday.getTime() &&
                (gcInventory ? e.id !== gcInventory.id : true)
        );
        return _lastEntryForSku;
    };

    const isDatatableDisabled = () => {
        for (const gcInventory of gcInventories) {
            if (gcInventory.isApproving || gcInventory.isDeleting) return true;
        }
        return false;
    };

    const validate = () => {
        let _formErrors = {
            activations: '',
            destroyed: '',
            lost: '',
            transferred: '',
            received: '',
            onHand: '',
            reason: '',
            gcInventory: '',
        };
        setFormErrors(_formErrors);
        let errors = false;
        const _sku = sku();
        if (_sku.detailed_form) {
            if (activations === '') {
                _formErrors.activations = 'activations_empty_error';
                errors = true;
            }
            if (destroyed === '') {
                _formErrors.destroyed = 'destroyed_empty_error';
                errors = true;
            }
            if (lost === '') {
                _formErrors.lost = 'lost_empty_error';
                errors = true;
            }
            if (transferred === '') {
                _formErrors.transferred = 'transferred_empty_error';
                errors = true;
            }
            if (received === '') {
                _formErrors.received = 'received_empty_error';
                errors = true;
            }
            if (discrepancy && !reason) {
                _formErrors.reason = 'reason_empty_error';
                errors = true;
            }
        }
        if (onHand === '') {
            _formErrors.onHand = 'onhand_empty_error';
            errors = true;
        }

        if (errors) {
            setFormErrors(_formErrors);
            setValidated(true);
            return false;
        }
        return true;
    };

    const approveGcInventory = (gcInventory) => {
        gcInventory.isApproving = true;
        approveGcInventoryMutation({
            variables: {
                id: gcInventory.id,
            },
        })
            .then((response) => {
                gcInventory.isApproving = false;
                if (response.errors) {
                    setInfoModalText('giftcard_approve_fail');
                    setInfoModalOptions({
                        header: <ErrorIcon fill="var(--bs-red)" />,
                        center: true,
                    });
                    setInfoModalIsOpen(true);
                } else {
                    setInfoModalText('giftcard_approve_success');
                    setInfoModalOptions({
                        header: <CheckIcon fill="var(--bs-green)" />,
                        center: true,
                    });
                    setInfoModalIsOpen(true);
                    loadGcInventories();
                }
            })
            .catch((error) => {
                console.log({ error });
                gcInventory.isApproving = false;
                setInfoModalText('giftcard_approve_fail');
                setInfoModalOptions({
                    header: <ErrorIcon fill="var(--bs-red)" />,
                    center: true,
                });
                setInfoModalIsOpen(true);
            });
    };

    const deleteGcInventory = () => {
        gcInventoryToDelete.isDeleting = true;
        deleteGcInventoryMutation({
            variables: {
                id: gcInventoryToDelete.id,
            },
        })
            .then((response) => {
                gcInventoryToDelete.isDeleting = false;
                if (response.errors) {
                    setInfoModalText('giftcard_delete_fail');
                    setInfoModalOptions({
                        header: <ErrorIcon fill="var(--bs-red)" />,
                        center: true,
                    });
                    setInfoModalIsOpen(true);
                } else {
                    setInfoModalText('giftcard_delete_success');
                    setInfoModalOptions({
                        header: <CheckIcon fill="var(--bs-green)" />,
                        center: true,
                    });
                    setInfoModalIsOpen(true);
                    loadGcInventories();
                }
            })
            .catch((error) => {
                gcInventoryToDelete.isDeleting = false;
                console.log({ error });
                setInfoModalText('giftcard_delete_fail');
                setInfoModalOptions({
                    header: <ErrorIcon fill="var(--bs-red)" />,
                    center: true,
                });
                setInfoModalIsOpen(true);
            })
            .finally(() => setGcInventoryToDelete(null));
    };

    const sendGcInventoryNotificationEmail = async (id) => {
        const { data: gcInventoryData } = await getGcInventoryById({ variables: { id } });
        if (!gcInventoryData || gcInventoryData.table.count === 0) return;

        const gcInventory = gcInventoryData.table.records[0].gc_inventory;
        const { data: gcInventoryEmailListData } = await getGcInventoryEmailListByProperty({
            variables: {
                property_id: gcInventory.property__c_id,
            },
        });

        if (!gcInventoryEmailListData || gcInventoryEmailListData.table.count === 0) return;

        const gcInventoryEmailList =
            gcInventoryEmailListData.table.records[0].gc_inventory_email_list;

        if (!gcInventoryEmailList) return;

        const emails = gcInventoryEmailList.emails.split(',').map((email) => email.trim());
        let data = {
            sku: gcInventory.sku_list[0].sku.description,
            property: gcInventory.property__c_list[0].property__c.name,
            on_hand: parseInt(gcInventory.on_hand),
            date: gcInventory.date,
            comment: gcInventory.comment,
            counted_by: gcInventory.propertyuser__c_list[0].propertyuser__c.name,
        };

        if (gcInventory.sku_list[0].sku.detailed_form) {
            data = {
                ...data,
                activated: parseInt(gcInventory.activated),
                destroyed: parseInt(gcInventory.destroyed),
                lost: parseInt(gcInventory.lost),
                transferred: parseInt(gcInventory.transferred),
                received: parseInt(gcInventory.received),
                reason: gcInventory.reason,
            };
        }

        fetch(`${process.env.REACT_APP_PROXY_URL || ''}/sendtemplateemail`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                emails,
                template: 'gcInventoryCreation',
                data,
            }),
        }).catch((error) => {
            console.log({ error });
        });
    };

    const createGcInventory = () => {
        setIsFormSubmitting(true);
        if (validate()) {
            const _sku = sku();
            let variables = {
                sku: _sku.id,
                on_hand: parseInt(onHand),
                comment,
                date: formatUTCDateTime(),
            };
            if (_sku.detailed_form) {
                variables = {
                    ...variables,
                    activated: parseInt(activations),
                    destroyed: parseInt(destroyed),
                    lost: parseInt(lost),
                    received: parseInt(received),
                    transferred: parseInt(transferred),
                    reason: discrepancy !== 0 ? reason : '',
                };
            }
            createGcInventoryMutation({
                variables,
            })
                .then((response) => {
                    if (response.errors) {
                        setFormErrors({
                            ...formErrors,
                            gcInventory: 'giftcard_create_fail',
                        });
                        setIsFormSubmitting(false);
                    } else {
                        setShowModal(false);
                        setInfoModalText('giftcard_create_success');
                        setInfoModalOptions({
                            header: <CheckIcon fill="var(--bs-green)" />,
                            center: true,
                        });
                        setInfoModalIsOpen(true);
                        loadGcInventories();
                        sendGcInventoryNotificationEmail(response.data.createRow.gc_inventory.id);
                    }
                })
                .catch((error) => {
                    console.log({ error });
                    setFormErrors({
                        ...formErrors,
                        gcInventory: 'giftcard_create_fail',
                    });
                    setIsFormSubmitting(false);
                });
        } else {
            setIsFormSubmitting(false);
        }
    };

    const editGcInventory = () => {
        setIsFormSubmitting(true);
        if (validate()) {
            updateGcInventoryMutation({
                variables: {
                    id: gcInventoryToEdit.id,
                    on_hand: parseInt(onHand),
                    activated: parseInt(activations),
                    destroyed: parseInt(destroyed),
                    lost: parseInt(lost),
                    received: parseInt(received),
                    transferred: parseInt(transferred),
                    reason: discrepancy !== 0 ? reason : '',
                    comment,
                    date: formatUTCDateTime(),
                },
            })
                .then((response) => {
                    if (response.errors) {
                        setFormErrors({
                            ...formErrors,
                            gcInventory: 'giftcard_update_fail',
                        });
                        setIsFormSubmitting(false);
                    } else {
                        setShowModal(false);
                        setInfoModalText('giftcard_update_success');
                        setInfoModalOptions({
                            header: <CheckIcon fill="var(--bs-green)" />,
                            center: true,
                        });
                        setInfoModalIsOpen(true);
                        loadGcInventories();
                    }
                })
                .catch((error) => {
                    console.log({ error });
                    setFormErrors({
                        ...formErrors,
                        gcInventory: 'giftcard_update_fail',
                    });
                    setIsFormSubmitting(false);
                });
        } else {
            setIsFormSubmitting(false);
        }
    };

    const resetForm = () => {
        setSkuIndex(0);
        setActivations(0);
        setDestroyed(0);
        setLost(0);
        setTransferred(0);
        setReceived(0);
        setComment('');
        setOnHand(0);
        setReason('');
    };

    const setModalFormValues = (gcInventory = null) => {
        setIsFormSubmitting(false);
        setValidated(false);
        if (gcInventory) {
            setSkuIndex(skus ? skus.findIndex((sku) => sku.id === gcInventory.sku.id) : 0);
            setActivations(parseInt(gcInventory.activated));
            setDestroyed(parseInt(gcInventory.destroyed));
            setLost(parseInt(gcInventory.lost));
            setTransferred(parseInt(gcInventory.transferred));
            setReceived(parseInt(gcInventory.received));
            setComment(gcInventory.comment);
            setOnHand(parseInt(gcInventory.on_hand));
            setReason(gcInventory.reason);
        } else {
            resetForm();
        }
    };

    const openModal = (modalMode, gcInventory = null) => {
        setGcInventoryToEdit(gcInventory);
        setModalMode(modalMode);
        assignModalTitle(modalMode, gcInventory);

        if (modalMode !== ModalModes.Create) {
            setExistingEntryForSku(null);
        } else {
            updateLastEntryAndCheckForExistingEntry();
        }
        if (gcInventory) {
            if (cachedGroupedGcInventories[gcInventory.sku.id]) {
                setLastEntryForSku(
                    findLastEntryForSku(cachedGroupedGcInventories[gcInventory.sku.id], gcInventory)
                );
            } else {
                setIsLoadingFormModal(true);
                getGcInventoriesBySkusForProperty({
                    variables: {
                        sku_ids: [gcInventory.sku.id],
                    },
                }).then((response) => {
                    const relatedGcInventories = response.data.table.records.map(
                        (r) => r.gc_inventory
                    );
                    setCachedGroupedGcInventories({
                        ...cachedGroupedGcInventories,
                        [gcInventory.sku.id]: relatedGcInventories,
                    });
                    setLastEntryForSku(findLastEntryForSku(relatedGcInventories, gcInventory));
                    setIsLoadingFormModal(false);
                });
            }
        }
        setModalFormValues(gcInventory);
        setShowModal(true);
    };

    const calculateOnHandValue = () => {
        if (lastEntryForSku) {
            const _activations = activations === '' ? 0 : parseInt(activations);
            const _destroyed = destroyed === '' ? 0 : parseInt(destroyed);
            const _lost = lost === '' ? 0 : parseInt(lost);
            const _transferred = transferred === '' ? 0 : parseInt(transferred);
            const _received = received === '' ? 0 : parseInt(received);
            const onHandCalculated =
                parseInt(lastEntryForSku.on_hand) -
                _activations -
                _destroyed -
                _lost -
                _transferred +
                _received;
            setOnHandCalculated(onHandCalculated);
            calculateDiscrepancy(onHandCalculated);
        } else {
            setOnHandCalculated(0);
            setDiscrepancy(0);
        }
    };

    const checkForExistingEntryForSkuThisMonth = (relatedGcInventories) => {
        setExistingEntryForSku(null);
        if (!relatedGcInventories || relatedGcInventories.length === 0) {
            return;
        }
        const today = new Date();
        for (const gcInventory of relatedGcInventories) {
            const gcInventoryDate = new Date(`${gcInventory.date}Z`);
            if (
                gcInventoryDate.getFullYear() === today.getFullYear() &&
                gcInventoryDate.getMonth() === today.getMonth()
            ) {
                setExistingEntryForSku({
                    ...gcInventory,
                    sku: skus.find((sku) => sku.id === gcInventory.sku),
                });
            }
        }
    };

    const calculateDiscrepancy = (onHandCalculated) => {
        const _onHand = isNaN(onHand) ? 0 : onHand;
        setDiscrepancy(_onHand - onHandCalculated);
    };

    const isReadOnly = () => {
        return modalMode === ModalModes.ReadOnly;
    };

    const loadGcInventories = async () => {
        setIsLoadingGcInventories(true);
        const { data, error } = await getGcInventoriesForProperty({
            variables: {
                offset: (page - 1) * rowsPerPage,
                limit: rowsPerPage,
            },
        });
        if (data && data.table) {
            setTotalGcInventoryCount(data.table.count);
            const gcInventories = await parseGcInventoriesResponse(data);
            const orderedGcInventories = [].concat(
                ...Object.values(groupGcInventoriesBySkuOrderByDate(gcInventories))
            );
            setGcInventories(orderedGcInventories);
            const _updatedGcInventories =
                await loadGcInventoryOnHandCalculated(orderedGcInventories);
            loadGcInventoryRevisionCounts(_updatedGcInventories);
        } else {
            console.log(error);
        }
        setIsLoadingGcInventories(false);
    };

    const sku = () => {
        if (!skus || skus.length === 0) return null;
        return skus[skuIndex];
    };

    const updateLastEntryAndCheckForExistingEntry = () => {
        const _sku = sku();
        if (_sku && !gcInventoryToEdit) {
            if (cachedGroupedGcInventories[_sku.id]) {
                setLastEntryForSku(findLastEntryForSku(cachedGroupedGcInventories[_sku.id]));
                checkForExistingEntryForSkuThisMonth(cachedGroupedGcInventories[_sku.id]);
            } else {
                setIsLoadingFormModal(true);
                getGcInventoriesBySkusForProperty({
                    variables: {
                        sku_ids: [_sku.id],
                    },
                }).then((response) => {
                    const relatedGcInventories = response.data.table.records.map(
                        (r) => r.gc_inventory
                    );
                    setCachedGroupedGcInventories({
                        ...cachedGroupedGcInventories,
                        [_sku.id]: relatedGcInventories,
                    });
                    setLastEntryForSku(findLastEntryForSku(relatedGcInventories));
                    checkForExistingEntryForSkuThisMonth(relatedGcInventories);
                    setIsLoadingFormModal(false);
                });
            }
        }
    };

    const loadCsvData = async () => {
        setCsvFileName(
            `gc_inventory_${property.name}_${formatUTCDateTime()}.csv`.replaceAll(' ', '_')
        );
        setIsLoadingCsv(true);
        const { data, error } = await getGcInventoriesForProperty({
            variables: {
                offset: 0,
                limit: 2000,
            },
        });

        if (data && data.table) {
            const gcInventories = await parseGcInventoriesResponse(data, true);
            const orderedGcInventories = [].concat(
                ...Object.values(groupGcInventoriesBySkuOrderByDate(gcInventories))
            );
            setCsvData(orderedGcInventories);
        } else {
            console.log({ error });
            setInfoModalText('csv_file_fail');
            setInfoModalOptions({
                header: <ErrorIcon fill="var(--bs-red)" />,
                center: true,
            });
            setInfoModalIsOpen(true);
        }
        setIsLoadingCsv(false);
    };

    useEffect(() => {
        if (csvData && csvData.length > 0) csvLinkRef.current.link.click();
    }, [csvData]);

    useEffect(() => {
        if (!skus || skus.length === 0) return;
        loadGcInventories();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (!skus || skus.length === 0) return;
        loadGcInventories();
    }, [skus]);

    useEffect(() => {
        updateLastEntryAndCheckForExistingEntry();
    }, [skuIndex]);

    useEffect(() => {
        setValidated(false);
        const _sku = sku();
        if (_sku && _sku.detailed_form) {
            calculateOnHandValue();
        }
    }, [activations, lost, destroyed, received, transferred, onHand, lastEntryForSku]);

    return (
        <div className="gc-inventory">
            <div
                className="alert alert-warning d-flex justify-content-between align-items-center mb-4"
                role="alert"
            >
                <div>
                    <strong>{t('legacy_view')}</strong>
                    {' — '}
                    {t('legacy_view_notice')}
                </div>
                <Link to="/giftcardinventory" className="btn btn-primary btn-sm ms-3 text-nowrap">
                    {t('go_to_current_version')}
                </Link>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="m-0">{t('gift_card_inventory')}</h3>
                <LoadingButton
                    onClick={() => loadCsvData()}
                    isLoading={isLoadingCsv}
                    variant="secondary"
                >
                    {t('download_csv')}
                </LoadingButton>
                <CSVLink
                    ref={csvLinkRef}
                    data={csvData}
                    headers={csvHeaders}
                    filename={csvFileName}
                    style={{ display: 'none' }}
                ></CSVLink>
            </div>
            {!(isLoadingSkus || isLoadingGcInventories) && (
                <Button className="w-100 mb-4" onClick={() => openModal(ModalModes.Create)}>
                    {t('create_giftcard_inventory')}
                </Button>
            )}
            <DataTable
                columns={columns}
                data={gcInventories}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                paginationTotalRows={totalGcInventoryCount}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage) => setRowsPerPage(currentRowsPerPage)}
                progressPending={isLoadingGcInventories}
                progressComponent={
                    <div className="d-flex justify-content-center align-items-center my-4">
                        <Spinner></Spinner>
                    </div>
                }
                highlightOnHover
                onRowClicked={(gcInventory) => {
                    !isDatatableDisabled() && openModal(ModalModes.ReadOnly, gcInventory);
                }}
                persistTableHead
                customStyles={{
                    rows: {
                        style: {
                            cursor: 'pointer',
                        },
                    },
                }}
                paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
            />
            <Modal show={showModal} onHide={() => setShowModal(false)} className="gc-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{modalTitle}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('sku')}
                            </Form.Label>
                            <Form.Select
                                disabled={isReadOnly() || gcInventoryToEdit}
                                value={skuIndex}
                                onChange={(event) => setSkuIndex(event.target.value)}
                            >
                                {skus.map((sku, i) => (
                                    <option value={i} key={i}>
                                        {sku.description}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {isLoadingFormModal ? (
                            <div className="my-4 d-flex justify-content-center">
                                <Spinner></Spinner>
                            </div>
                        ) : (
                            <>
                                {existingEntryForSku ? (
                                    <div className="mb-2 d-flex flex-column">
                                        {t('one_entry_per_month')}
                                        {!existingEntryForSku.approved_by && (
                                            <a
                                                href="#"
                                                onClick={() =>
                                                    openModal(ModalModes.Edit, existingEntryForSku)
                                                }
                                            >
                                                {t('edit_existing_entry')}
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {sku() && sku().detailed_form && (
                                            <>
                                                <Form.Group className="mb-3 position-relative">
                                                    <Form.Label>
                                                        <span className="required-field">*</span>{' '}
                                                        {t('activations')}
                                                    </Form.Label>
                                                    {lastEntryForSku && (
                                                        <div className="previous-value">
                                                            {parseInt(lastEntryForSku.activated)}
                                                        </div>
                                                    )}
                                                    <Form.Control
                                                        isInvalid={
                                                            validated && formErrors.activations
                                                        }
                                                        disabled={isReadOnly()}
                                                        type="number"
                                                        value={activations}
                                                        onChange={(e) =>
                                                            setActivations(e.target.value)
                                                        }
                                                    />
                                                    {formErrors.activations && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {t(formErrors.activations)}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                                <Form.Group className="mb-3 position-relative">
                                                    <Form.Label>
                                                        <span className="required-field">*</span>{' '}
                                                        {t('destroyed')}
                                                    </Form.Label>
                                                    {lastEntryForSku && (
                                                        <div className="previous-value">
                                                            {parseInt(lastEntryForSku.destroyed)}
                                                        </div>
                                                    )}
                                                    <Form.Control
                                                        isInvalid={
                                                            validated && formErrors.destroyed
                                                        }
                                                        disabled={isReadOnly()}
                                                        type="number"
                                                        value={destroyed}
                                                        onChange={(e) =>
                                                            setDestroyed(e.target.value)
                                                        }
                                                    />
                                                    {formErrors.destroyed && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {t(formErrors.destroyed)}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                                <Form.Group className="mb-3 position-relative">
                                                    <Form.Label>
                                                        <span className="required-field">*</span>{' '}
                                                        {t('lost')}
                                                    </Form.Label>
                                                    {lastEntryForSku && (
                                                        <div className="previous-value">
                                                            {parseInt(lastEntryForSku.lost)}
                                                        </div>
                                                    )}
                                                    <Form.Control
                                                        isInvalid={validated && formErrors.lost}
                                                        disabled={isReadOnly()}
                                                        type="number"
                                                        value={lost}
                                                        onChange={(e) => setLost(e.target.value)}
                                                    />
                                                    {formErrors.lost && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {t(formErrors.lost)}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                                <Form.Group className="mb-3 position-relative">
                                                    <Form.Label>
                                                        <span className="required-field">*</span>{' '}
                                                        {t('transferred')}
                                                    </Form.Label>
                                                    {lastEntryForSku && (
                                                        <div className="previous-value">
                                                            {parseInt(lastEntryForSku.transferred)}
                                                        </div>
                                                    )}
                                                    <Form.Control
                                                        isInvalid={
                                                            validated && formErrors.transferred
                                                        }
                                                        disabled={isReadOnly()}
                                                        type="number"
                                                        value={transferred}
                                                        onChange={(e) =>
                                                            setTransferred(e.target.value)
                                                        }
                                                    />
                                                    {formErrors.transferred && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {t(formErrors.transferred)}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                                <Form.Group className="mb-3 position-relative">
                                                    <Form.Label>
                                                        <span className="required-field">*</span>{' '}
                                                        {t('received')}
                                                    </Form.Label>
                                                    {lastEntryForSku && (
                                                        <div className="previous-value">
                                                            {parseInt(lastEntryForSku.received)}
                                                        </div>
                                                    )}
                                                    <Form.Control
                                                        isInvalid={validated && formErrors.received}
                                                        disabled={isReadOnly()}
                                                        type="number"
                                                        value={received}
                                                        onChange={(e) =>
                                                            setReceived(e.target.value)
                                                        }
                                                    />
                                                    {formErrors.received && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {t(formErrors.received)}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </>
                                        )}
                                        <Form.Group className="mb-3">
                                            <Form.Label>{t('comment')}</Form.Label>
                                            <Form.Control
                                                disabled={isReadOnly()}
                                                type="text"
                                                value={comment}
                                                onChange={(e) => setComment(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label>
                                                <span className="required-field">*</span>{' '}
                                                {t('on_hand')}
                                            </Form.Label>
                                            {lastEntryForSku && (
                                                <div className="previous-value">
                                                    {parseInt(lastEntryForSku.on_hand)}
                                                </div>
                                            )}
                                            <Form.Control
                                                isInvalid={validated && formErrors.onHand}
                                                disabled={isReadOnly()}
                                                type="number"
                                                value={onHand}
                                                onChange={(e) => setOnHand(e.target.value)}
                                            />
                                            {formErrors.onHand && (
                                                <Form.Control.Feedback type="invalid">
                                                    {t(formErrors.onHand)}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                        {sku() && sku().detailed_form && lastEntryForSku && (
                                            <>
                                                <hr className="mt-4" />
                                                <div className="d-flex">
                                                    <div className="calc-col header">
                                                        {t('on_hand_short')}
                                                    </div>
                                                    <div className="operation-col"></div>
                                                    <div className="calc-col header long">
                                                        {t('on_hand_calculated')}
                                                    </div>
                                                    <div className="operation-col"></div>
                                                    <div className="calc-col header">
                                                        {t('discrepancy')}
                                                    </div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="calc-col">{onHand}</div>
                                                    <div className="operation-col">-</div>
                                                    <div className="calc-col long">
                                                        {onHandCalculated}
                                                    </div>
                                                    <div className="operation-col">=</div>
                                                    <div className="calc-col">{discrepancy}</div>
                                                </div>
                                            </>
                                        )}
                                        {sku() && sku().detailed_form && discrepancy !== 0 && (
                                            <Form.Group className="my-3">
                                                <Form.Label>
                                                    <span className="required-field">*</span>{' '}
                                                    {t('reason')}
                                                </Form.Label>
                                                <Form.Control
                                                    isInvalid={validated && formErrors.reason}
                                                    disabled={isReadOnly()}
                                                    type="text"
                                                    value={reason}
                                                    onChange={(e) => setReason(e.target.value)}
                                                />
                                                {formErrors.reason && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {t(formErrors.reason)}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {formErrors.gcInventory && (
                            <div
                                className="invalid-feedback mt-3 fw-bold"
                                style={{
                                    display: formErrors.gcInventory ? 'block' : 'none',
                                }}
                            >
                                {t(formErrors.gcInventory)}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        onClick={() => {
                            switch (modalMode) {
                                case ModalModes.Create:
                                    createGcInventory();
                                    break;
                                case ModalModes.Edit:
                                    editGcInventory();
                                    break;
                                case ModalModes.ReadOnly:
                                default:
                                    setModalMode(ModalModes.Edit);
                                    assignModalTitle(ModalModes.Edit, gcInventoryToEdit);
                                    break;
                            }
                        }}
                        isLoading={isFormSubmitting}
                        isDisabled={
                            isLoadingFormModal ||
                            existingEntryForSku ||
                            (gcInventoryToEdit && gcInventoryToEdit.approved_by)
                        }
                    >
                        {t(modalMode === ModalModes.Create ? 'create' : 'edit')}
                    </LoadingButton>
                </Modal.Footer>
            </Modal>
            <Modal
                size="xl"
                show={gcInventoryInAuditModal}
                onHide={() => setGcInventoryInAuditModal(null)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{auditModalTitle}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingAudits ? (
                        <div className="d-flex justify-content-center my-2">
                            <Spinner></Spinner>
                        </div>
                    ) : (
                        <>
                            {showAuditErrorMsg ? (
                                <div
                                    className="invalid-feedback my-2 fw-bold text-center"
                                    style={{
                                        display: showAuditErrorMsg ? 'block' : 'none',
                                    }}
                                >
                                    {t('revisions_load_fail')}
                                </div>
                            ) : (
                                <Table borderless striped className="m-0">
                                    <thead>
                                        <tr>
                                            <th>{t('date')}</th>
                                            <th>{t('on_hand_short')}</th>
                                            {gcInventoryInAuditModal &&
                                                gcInventoryInAuditModal.sku.detailed_form && (
                                                    <>
                                                        <th>{t('activations')}</th>
                                                        <th>{t('destroyed')}</th>
                                                        <th>{t('lost')}</th>
                                                        <th>{t('received')}</th>
                                                        <th>{t('transferred')}</th>
                                                        <th>{t('reason')}</th>
                                                    </>
                                                )}
                                            <th>{t('comment')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {audits.map((audit, i) => {
                                            return (
                                                <tr key={i}>
                                                    <td>{audit.date}</td>
                                                    <td>{parseInt(audit.on_hand)}</td>
                                                    {gcInventoryInAuditModal &&
                                                        gcInventoryInAuditModal.sku
                                                            .detailed_form && (
                                                            <>
                                                                <td>{parseInt(audit.activated)}</td>
                                                                <td>{parseInt(audit.destroyed)}</td>
                                                                <td>{parseInt(audit.lost)}</td>
                                                                <td>{parseInt(audit.received)}</td>
                                                                <td>
                                                                    {parseInt(audit.transferred)}
                                                                </td>
                                                                <td>{audit.reason}</td>
                                                            </>
                                                        )}
                                                    <td>{audit.comment}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setGcInventoryInAuditModal(null)}>
                        {t('close')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                setOpen={setIsDeleteConfirmationModalOpen}
                onConfirm={deleteGcInventory}
                options={{
                    center: true,
                    header: <WarningIcon fill="var(--bs-yellow)" height={48} width={48} />,
                }}
            >
                <p>{t('confirm_delete_entry')}</p>
            </ConfirmationModal>
            <InfoModal
                isOpen={infoModalIsOpen}
                setOpen={setInfoModalIsOpen}
                options={infoModalOptions}
            >
                {t(infoModalText)}
            </InfoModal>
        </div>
    );
};

export default GiftCardInventory;
